import { format } from "date-fns";
import { makeAutoObservable, runInAction, when } from "mobx";
import RootStore from "stores/RootStore";
import { compareNaturally, MM_REFRESH_MIN_INTERVAL_MS } from "./MMProductionStore";
import { mmErrorMessage } from "./mmErrorMessage";

// The API's DATE columns come back as full ISO datetimes
// (e.g. "2026-06-24T00:00:00.000Z"). The backend validates writes as
// `format: 'date'` (yyyy-MM-dd), so a re-sent datetime fails validation on
// patch/create. Normalize to a plain date string everywhere we read/write
// these. Do NOT apply to createdAt/updatedAt (those are datetimes).
const toDateOnly = (v: unknown): string =>
  v == null ? "" : String(v).split("T")[0];

// `mmPallets.items` and `mmLoads.freeRackItems` are both client-owned JSON that
// no server resolver parses in either direction. Malformed content is possible
// in principle, so failing soft to an empty list beats taking the whole Loads
// page down with a parse error on one row.
const parseItemsJson = (value: unknown): MMPalletItem[] => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value as MMPalletItem[];
  try {
    const parsed = JSON.parse(String(value));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    console.warn("Unparseable items JSON on a load/pallet; treating as empty");
    return [];
  }
};

// ============================================================================
// TYPES
// ============================================================================

// "stored" (Creede, 2026-10-16): "We'll make a load… be stored and we will bill
// for it. And then they will want it at a later date… It doesn't change any of
// your delivered quantities." Billed, not delivered.
export type LoadStatus = "planned" | "loaded" | "in_transit" | "delivered" | "stored";

export interface MMPalletItem {
  markNumber: string;
  /** Pieces LOADED onto the pallet. What left the yard. */
  quantity: number;
  cubicFeet: number;
  /**
   * Which half of a paired job this piece belongs to (item 12).
   *
   * A load spans the pair — "It's one shipment, and it can be both products"
   * — but billing, contract ceilings and delivered counts stay per job, so
   * every piece has to say which job it came off.
   *
   * `undefined` means the load's own `projectId`, which is what every row
   * written before this existed is: no backfill, and an unpaired job never
   * needs the field at all. Read it through `itemProjectId`, never directly.
   *
   * `mmPallets.items` is client-owned JSON that no server resolver parses,
   * so this costs no migration.
   */
  projectId?: string;
  /**
   * Pieces that actually ARRIVED, when that differs from what was loaded.
   * undefined means "as loaded", which is the overwhelming majority.
   *
   * Damage surfaces days after delivery — "the truck driver braked too hard at
   * one point and a pallet fell over", or "the straps ate into the corners, and
   * they're screwed up and we've got to remake them" (Creede, 2026-08-14). He
   * records that in an Excel cell comment today.
   *
   * A separate field rather than editing `quantity`, because both facts matter:
   * what shipped drives the load list and the over-delivery warning, what
   * arrived drives the job's delivered counts.
   */
  deliveredQuantity?: number;
  /** Why the delivered count differs. Required whenever it does. */
  deliveryNote?: string;
}

export interface MMPallet {
  id: string;
  loadId: string;
  palletNumber: number;
  items: MMPalletItem[];
  totalCubicFeet: number;
  /** The S-number it had while staged, if it was (2026-10-16). */
  stagedNumber?: number | null;
}

/**
 * A pallet made up on the yard before anyone knows which truck it goes on
 * (2026-10-16 item 10). Creede: "I've got product on the yard, and my guys are
 * ready to palletize… But I don't know which pallets are going to go on which
 * loads… number them in order: S1… And then, when they're dragged onto a load,
 * you can number them for that load." Never free rack: "anything that would be
 * free rack is going to just sit on the yard."
 */
export interface MMStagedPallet {
  id: string;
  projectId: string;
  stagedNumber: number;
  items: MMPalletItem[];
  totalCubicFeet: number;
}

// A pallet's rows in mark order, on every write (2026-10-16 item 15): "it sorts
// itself when you initially create it. But when you… edit a pallet, it comes
// back and doesn't re-sort." Nothing sorted on create either -- it only looked
// sorted because marks are usually dragged in order.
const sortedItems = (items: MMPalletItem[]): MMPalletItem[] =>
  [...items].sort((a, b) => compareNaturally(a.markNumber, b.markNumber));

export interface MMLoad {
  id: string;
  projectId: string;
  loadNumber: string;
  shipDate: string;
  carrier: string;
  isOversized: boolean;
  contactName?: string;
  contactPhone?: string;
  pallets: MMPallet[];
  // Pieces that ride on boards rather than a pallet — Creede's 10-ft columns:
  // "This 10-foot column piece… will actually sit on just boards on the trailer
  // and we'll stack them up… we have no way to add those sort of pieces to our
  // loads right now. It makes me put them on a pallet."
  //
  // Same item shape as a pallet's, but deliberately NOT modelled as a pallet:
  // `mmPallets.palletNumber` is INT NOT NULL with no default and no unique
  // constraint, so a sentinel free-rack number would be a real integer
  // competing with the numbers Dutch and Creede make up in the yard, and
  // nothing at the database level would stop the collision.
  //
  // Read sites use `?? []`. The type says non-optional and `loadData` and
  // `createLoad` both populate it, but this field was ADDED to an existing
  // type — every load object written before 2026-08-10 silently lacks it, and
  // TypeScript only catches the construction sites it can see. The failure
  // mode is a thrown reduce inside the pallet dialog and the print path.
  freeRackItems: MMPalletItem[];
  totalCubicFeet: number;
  invoiceAmount: number;
  // Manual billed amount. undefined/null = "not overridden" (bill the
  // auto-calculated invoiceAmount). Zero IS a legitimate override.
  invoiceOverride?: number;
  status: LoadStatus;
  // When each event happened, yyyy-MM-dd; null = not recorded (2026-10-16
  // item 37). Creede: "all delivered and stored loads need a date for that
  // event. Then a separate date for billed… the billing date, date paid… a
  // separate delivery date once the load is actually delivered (on stored but
  // not deliv. loads)." A stored load later delivered keeps both.
  storedDate?: string | null;
  deliveredDate?: string | null;
  billedDate?: string | null;
  paidDate?: string | null;
}

// One place a searched mark sits (item 27). `load` is null on a staged pallet.
export interface MarkSearchHit {
  load: MMLoad | null;
  where: string;
  markNumber: string;
  quantity: number;
}

export const LOAD_EVENT_DATE_FIELDS = ["storedDate", "deliveredDate", "billedDate", "paidDate"] as const;
export type LoadEventDateField = (typeof LOAD_EVENT_DATE_FIELDS)[number];

// ============================================================================
// CONSTANTS
// ============================================================================

export const CARRIERS = [
  "LTL Stealth",
  "TL Stealth",
  "Customer Pick Up",
  "Third Party",
];

// Weight capacities in lbs (null = no limit / unknown)
export const CARRIER_WEIGHT_CAPACITIES: Record<string, number | null> = {
  "LTL Stealth": 11000,
  "TL Stealth": 41250,
  "Customer Pick Up": null,
  "Third Party": null,
};

// Loads that are ready to bill: they arrived, or they are stored on the yard
// for the customer to take later. Since item 37 (Creede, 10-23) neither status
// means BILLED -- a load is billed when it has a billed date: "Until that
// point though these events occur separately."
export const BILLABLE_STATUSES: readonly LoadStatus[] = ["delivered", "stored"];

export interface BillingLine {
  load: MMLoad;
  amount: number;
  cubicFeet: number;
  /** Has a billed date (item 37). */
  billed: boolean;
  /** Delivered or stored: ready to bill if not billed yet. */
  billable: boolean;
  /** Has a paid date. */
  paid: boolean;
}

// Loads whose pallets can still be relabelled: not yet out of the yard
// (2026-10-16 item 38).
export const UNDELIVERED_STATUSES: readonly LoadStatus[] = ["planned", "loaded", "in_transit"];

export const LOAD_STATUS_LABELS: Record<LoadStatus, string> = {
  planned: "Planned",
  loaded: "Loaded",
  in_transit: "In Transit",
  delivered: "Delivered",
  stored: "Stored",
};

// A load whose ship date has passed with nobody having said whether it arrived.
// Module-level so the store and the Loads table share ONE definition of the
// question — the row component takes no store injection.
//
// Local time, not toISOString(): the latter is UTC, which in US Central rolls
// over to tomorrow in the early evening and would start asking about today's
// loads before the day was out.
// A stored load is waiting on the CUSTOMER, on purpose -- never asked.
export const isAwaitingDeliveryConfirmation = (load: MMLoad): boolean =>
  load.status !== "delivered" &&
  load.status !== "stored" &&
  !!load.shipDate &&
  load.shipDate < format(new Date(), "yyyy-MM-dd");

// ============================================================================
// STORE
// ============================================================================

class MMLoadStore {
  loads: MMLoad[] = [];
  stagedPallets: MMStagedPallet[] = [];
  selectedProjectId: string | null = null;
  selectedLoadId: string | null = null;
  filterByStatus: LoadStatus | "all" = "all";
  searchTerm: string = "";
  isLoading: boolean = false;
  error: string | null = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    this.rootStore = rootStore;

    // Auto-load when authenticated
    when(
      () => !!(this.rootStore.authStore as any)?.isSignedIn,
      () => this.loadData()
    );
  }

  // ========================================
  // SERVICE HELPERS
  // ========================================

  private get loadsService() {
    return this.rootStore.app.service("mm-loads");
  }

  private get palletsService() {
    return this.rootStore.app.service("mm-pallets");
  }


  // ========================================
  // REFRESH (item 18)
  // ========================================
  //
  // Creede, 2026-09-15: "I was just having some refresh issues when I update the
  // status of a load... if I went back and made it not delivered and went back
  // into the job, it wouldn't populate until I... it's just a slow refresh, I
  // guess. I would wait a couple minutes and it'd be fine."
  //
  // It was not slowness. Every mm-* service declares `events: []`, so the server
  // broadcasts nothing, the stores register no socket listeners, and `loadData`
  // ran EXACTLY ONCE per tab -- from the constructor, on sign-in. A change made
  // in one tab (or by Dutch, in his own session) never arrived in another at
  // all, and the couple of minutes was most likely him deciding to reload.
  //
  // The cheap half of the fix, and deliberately the cheap half: re-read on
  // entering the screen, plus the header's refresh button, which re-runs the
  // same route hook. Real-time events are the correct answer and a bigger
  // change -- every service becomes a broadcaster, and these stores patch their
  // own state optimistically, so incoming events would have to stop fighting
  // those local merges. That belongs in a cycle with its own testing.
  lastLoadedAt = 0;

  /**
   * Re-read everything this store owns.
   *
   * THROTTLED, because `beforeEnter` fires on every navigation and this pulls
   * several thousand rows: tab-hopping between Tickets and Loads would
   * otherwise fire a burst against an API that allows 100 requests a minute.
   * The refresh button passes `force`, so there is always a way to insist.
   */
  refresh = async ({ force = false }: { force?: boolean } = {}) => {
    if (this.isLoading) return;
    if (!force && Date.now() - this.lastLoadedAt < MM_REFRESH_MIN_INTERVAL_MS) {
      return;
    }
    await this.loadData();
  };

  // ========================================
  // DATA LOADING
  // ========================================

  loadData = async () => {
    if (this.isLoading) return;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const [loadsResult, palletsResult] = await Promise.all([
        this.loadsService.find({ query: { $limit: 2000 } }),
        this.palletsService.find({ query: { $limit: 10000 } }),
      ]);

      const loadsData: any[] = Array.isArray(loadsResult)
        ? loadsResult
        : (loadsResult as any).data || [];
      const palletsData: any[] = Array.isArray(palletsResult)
        ? palletsResult
        : (palletsResult as any).data || [];

      // Group pallets by loadId. A pallet with NO load is staged (10-16): it
      // goes to its own list rather than being dropped for want of a load.
      const palletsByLoad = new Map<string, MMPallet[]>();
      const staged: MMStagedPallet[] = [];
      palletsData.forEach((p: any) => {
        if (!p.loadId) {
          if (p.projectId) {
            staged.push({
              id: p.palletId,
              projectId: p.projectId,
              stagedNumber: Number(p.stagedNumber) || 0,
              items: typeof p.items === "string" ? JSON.parse(p.items) : p.items || [],
              totalCubicFeet: Number(p.totalCubicFeet) || 0,
            });
          }
          return;
        }
        const pallet: MMPallet = {
          id: p.palletId,
          loadId: p.loadId,
          palletNumber: Number(p.palletNumber) || 0,
          items:
            typeof p.items === "string"
              ? JSON.parse(p.items)
              : p.items || [],
          totalCubicFeet: Number(p.totalCubicFeet) || 0,
          stagedNumber: p.stagedNumber != null ? Number(p.stagedNumber) : null,
        };
        if (!palletsByLoad.has(p.loadId)) {
          palletsByLoad.set(p.loadId, []);
        }
        palletsByLoad.get(p.loadId)!.push(pallet);
      });

      // Sort pallets by palletNumber within each load
      palletsByLoad.forEach((pallets) => {
        pallets.sort((a, b) => a.palletNumber - b.palletNumber);
      });

      const loads: MMLoad[] = loadsData.map((l: any) => ({
        id: l.loadId,
        projectId: l.projectId,
        loadNumber: l.loadNumber,
        shipDate: toDateOnly(l.shipDate),
        carrier: l.carrier,
        isOversized: !!l.isOversized,
        contactName: l.contactName || undefined,
        contactPhone: l.contactPhone || undefined,
        pallets: palletsByLoad.get(l.loadId) || [],
        freeRackItems: parseItemsJson(l.freeRackItems),
        totalCubicFeet: Number(l.totalCubicFeet) || 0,
        invoiceAmount: Number(l.invoiceAmount) || 0,
        // No || 0 here — zero is a legitimate override; absent means none.
        invoiceOverride:
          l.invoiceOverride != null ? Number(l.invoiceOverride) : undefined,
        status: l.status || "planned",
        storedDate: toDateOnly(l.storedDate) || null,
        deliveredDate: toDateOnly(l.deliveredDate) || null,
        billedDate: toDateOnly(l.billedDate) || null,
        paidDate: toDateOnly(l.paidDate) || null,
      }));

      runInAction(() => {
        this.loads = loads;
        this.stagedPallets = staged.sort((a, b) => a.stagedNumber - b.stagedNumber);
        this.isLoading = false;
        this.lastLoadedAt = Date.now();
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.lastLoadedAt = Date.now();
        this.error =
          error instanceof Error ? error.message : "Failed to load loads";
      });
      console.error("Failed to load loads:", error);
    }
  };

  // ========================================
  // COMPUTED
  // ========================================

  // Project-scoped loads with the status filter applied but NOT the search
  // term, so summary stats stay stable while searching.
  private get statusFilteredLoads(): MMLoad[] {
    if (!this.selectedProjectId) return [];
    // Item 12: the pair's loads are ONE list. Selecting either half shows the
    // same trucks, because a truck carrying both products is one shipment.
    const scope = this.projectScope(this.selectedProjectId);
    let result = this.loads.filter((l) => scope.includes(l.projectId));

    if (this.filterByStatus !== "all") {
      result = result.filter((l) => l.status === this.filterByStatus);
    }

    return result;
  }

  get loadsByProject(): MMLoad[] {
    let result = this.statusFilteredLoads;

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(
        (l) =>
          (l.loadNumber || "").toLowerCase().includes(term) ||
          (l.carrier || "").toLowerCase().includes(term) ||
          l.pallets.some((p) =>
            p.items.some((i) =>
              (i.markNumber || "").toLowerCase().includes(term)
            )
          ) ||
          // Free rack too (item 27): a piece on boards is still on the load.
          (l.freeRackItems ?? []).some((i) =>
            (i.markNumber || "").toLowerCase().includes(term)
          )
      );
    }

    return [...result].sort((a, b) => {
      // Sort by load number (numeric first, then MU loads)
      const aNum = parseInt(a.loadNumber);
      const bNum = parseInt(b.loadNumber);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      if (!isNaN(aNum)) return -1;
      if (!isNaN(bNum)) return 1;
      return a.loadNumber.localeCompare(b.loadNumber);
    });
  }

  /**
   * Where a mark is, across the job's loads and pallets (2026-10-16 item 27).
   * Creede: "I've got 12 loads and A24 is on three different loads… search a
   * mark and populate which loads/pallets… and the quantity of pieces on that
   * pallet." Driven by the Loads search box; free rack and staged pallets
   * included. Empty unless the term matches a mark.
   */
  get markSearchHits(): MarkSearchHit[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term || !this.selectedProjectId) return [];
    const matches = (item: MMPalletItem) => (item.markNumber || "").toLowerCase().includes(term);
    const hits: MarkSearchHit[] = [];
    for (const load of this.loadsByProject) {
      for (const pallet of [...load.pallets].sort((a, b) => a.palletNumber - b.palletNumber)) {
        for (const item of pallet.items.filter(matches)) {
          hits.push({ load, where: `Pallet ${pallet.palletNumber}`, markNumber: item.markNumber, quantity: item.quantity });
        }
      }
      for (const item of (load.freeRackItems ?? []).filter(matches)) {
        hits.push({ load, where: "Free rack", markNumber: item.markNumber, quantity: item.quantity });
      }
    }
    for (const pallet of this.stagedPalletsFor(this.selectedProjectId)) {
      for (const item of pallet.items.filter(matches)) {
        hits.push({ load: null, where: `Staged S${pallet.stagedNumber}`, markNumber: item.markNumber, quantity: item.quantity });
      }
    }
    return hits;
  }

  get selectedLoad(): MMLoad | null {
    if (!this.selectedLoadId) return null;
    return this.loads.find((l) => l.id === this.selectedLoadId) || null;
  }

  getLoadById = (id: string): MMLoad | undefined => {
    return this.loads.find((l) => l.id === id);
  };

  // The amount actually billed: manual override when set (including 0),
  // otherwise the auto-calculated cubic-foot invoiceAmount.
  effectiveInvoiceAmount = (load: MMLoad): number => {
    return load.invoiceOverride ?? load.invoiceAmount;
  };

  // Loads whose `projectId` IS this project. Deliberately literal — anything
  // that divides money by job (contract ceilings, re-pricing at a job's rate)
  // must not silently take in the paired half's loads.
  getLoadsByProject = (projectId: string): MMLoad[] => {
    return this.loads.filter((l) => l.projectId === projectId);
  };

  // The jobs that ship together: this project plus its paired half.
  // Falls back to the project alone when the project store is unavailable, so
  // every scoped read degrades to today's behaviour rather than to nothing.
  private projectScope = (projectId: string): string[] => {
    return this.projectStore?.projectScopeFor(projectId) ?? [projectId];
  };

  // Every load carrying pieces from either half of the pair. This is the right
  // read for anything about SHIPPING — what is on a truck, what has left, what
  // load number comes next. It is the wrong read for anything about MONEY.
  getLoadsInScope = (projectId: string): MMLoad[] => {
    const scope = this.projectScope(projectId);
    return this.loads.filter((l) => scope.includes(l.projectId));
  };

  // Which job a piece on a load belongs to. Rows written before item 12 carry
  // no projectId and belong to the load's own job, which is what they were.
  private itemProjectId = (load: MMLoad, item: MMPalletItem): string =>
    item.projectId || load.projectId;

  // Every piece riding on a load, pallets and free rack alike, as one list.
  private allItemsOnLoad = (load: MMLoad): MMPalletItem[] => [
    ...load.pallets.flatMap((pallet) => pallet.items),
    ...(load.freeRackItems ?? []),
  ];

  // Cubic feet on this load belonging to one job. The pair's halves are priced
  // at their own rates, so the split has to survive all the way to the invoice.
  cubicFeetOnLoadForProject = (load: MMLoad, projectId: string): number => {
    const total = this.allItemsOnLoad(load)
      .filter((item) => this.itemProjectId(load, item) === projectId)
      .reduce((sum, item) => sum + (item.cubicFeet || 0), 0);
    return Math.round(total * 100) / 100;
  };

  // What a load bills, priced per job at each job's own rate and summed.
  //
  // For an unpaired load — every item resolving to `load.projectId` — this is
  // arithmetically identical to pricing the whole load at one rate, so nothing
  // about an ordinary job's invoice changes.
  private computedInvoiceFor = (load: MMLoad): number => {
    // Mock-up pieces bill at the mock-up rate (2026-10-16 item 22), so they are
    // summed apart from the rest of their job's cubic feet.
    const byProject = new Map<string, number>();
    const mockupByProject = new Map<string, number>();
    for (const item of this.allItemsOnLoad(load)) {
      const pid = this.itemProjectId(load, item);
      const bucket = this.productionStore?.isMockupMark?.(pid, item.markNumber) ? mockupByProject : byProject;
      bucket.set(pid, (bucket.get(pid) ?? 0) + (item.cubicFeet || 0));
    }
    // No items to split by — price the load's own stored cubic feet at its own
    // job's rate, which is exactly what this did before item 12.
    // `totalCubicFeet` and the items agree whenever
    // recalculateAndPersistLoadTotals wrote them, but a load can carry a total
    // with no items behind it, and pricing that to zero would silently wipe a
    // real invoice.
    if (byProject.size === 0 && mockupByProject.size === 0) {
      return this.priceCubicFeet(load.projectId, load.totalCubicFeet);
    }

    let total = 0;
    byProject.forEach((cuFt, pid) => {
      total += this.priceCubicFeet(pid, cuFt);
    });
    mockupByProject.forEach((cuFt, pid) => {
      const mockupRate = this.productionStore?.mockupRateFor?.(pid);
      total +=
        mockupRate != null
          ? Math.round(cuFt * mockupRate * 100) / 100
          : this.priceCubicFeet(pid, cuFt);
    });
    return Math.round(total * 100) / 100;
  };

  // One job's share of what a load actually bills.
  //
  // Apportions the STORED figure — `invoiceOverride ?? invoiceAmount` — rather
  // than re-deriving it from rates. That figure is the money: an override is a
  // deliberate billing decision, and a load priced before a rate change was
  // billed at the old rate. Re-deriving here would quietly disagree with the
  // invoice on exactly the loads a contract ceiling exists to catch.
  //
  // A load is one truck with one number on it, so the only division that means
  // anything is how the truck itself divides: by each job's cubic feet.
  //
  // With no items to divide by, the whole figure belongs to the load's own job
  // — which is every load on an unpaired job, so nothing about an ordinary
  // contract ceiling changes.
  invoiceShareForProject = (load: MMLoad, projectId: string): number => {
    const billed = this.effectiveInvoiceAmount(load);

    const total = this.allItemsOnLoad(load).reduce(
      (sum, item) => sum + (item.cubicFeet || 0),
      0
    );
    if (total <= 0) {
      return load.projectId === projectId ? billed : 0;
    }

    const mine = this.cubicFeetOnLoadForProject(load, projectId);
    if (mine <= 0) return 0;
    // The whole truck is this job's — return the figure untouched rather than
    // a rounded ratio of itself.
    if (mine >= total) return billed;
    return Math.round(billed * (mine / total) * 100) / 100;
  };

  // Pallet numbers run on across the job's loads (Creede, 2026-10-16): "load
  // one has pallet one through 10. I'd like load two to have pallet 11 through
  // 20… when a customer calls, I can say, yeah, man, those pieces were on
  // pallet 21… otherwise there's 18 pallet ones." Across the pair, like load
  // numbers. Existing pallets keep their numbers: a slip is a label already on
  // a pallet in the yard.
  nextPalletNumber = (projectId: string): number => {
    const numbers = this.getLoadsInScope(projectId).flatMap((l) =>
      l.pallets.map((p) => p.palletNumber ?? 0)
    );
    return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  };

  // Load numbers run across the pair, not per half: two trucks on one contract
  // must not both be "Load 1".
  getNextLoadNumber = (projectId: string): string => {
    const projectLoads = this.getLoadsInScope(projectId);
    const numericLoads = projectLoads
      .map((l) => parseInt(l.loadNumber))
      .filter((n) => !isNaN(n));

    if (numericLoads.length === 0) return "1";
    return (Math.max(...numericLoads) + 1).toString();
  };

  // Cubic feet that actually ARRIVED on a load.
  //
  // `item.cubicFeet` is the line total for all pieces of that mark on the
  // pallet (perPiece x quantity), so a short delivery scales it by the fraction
  // that turned up rather than subtracting a per-piece figure that is not
  // stored.
  //
  // Falls back to the full line whenever nothing was corrected, which is the
  // overwhelming majority, so this equals load.totalCubicFeet for an untouched
  // load.
  deliveredCubicFeetForLoad = (load: MMLoad): number => {
    const lineCuFt = (item: MMPalletItem): number => {
      const delivered = item.deliveredQuantity ?? item.quantity;
      if (delivered === item.quantity) return item.cubicFeet || 0;
      // A zero-quantity line has no per-piece figure to scale; treat it as
      // nothing delivered rather than dividing by zero.
      if (!item.quantity) return 0;
      return ((item.cubicFeet || 0) / item.quantity) * delivered;
    };
    const total =
      load.pallets.reduce(
        (sum, pallet) => sum + pallet.items.reduce((s, i) => s + lineCuFt(i), 0),
        0
      ) + (load.freeRackItems ?? []).reduce((s, i) => s + lineCuFt(i), 0);
    return Math.round(total * 100) / 100;
  };

  get loadStats() {
    // Stats reflect the full project list, not the search-filtered view.
    const loads = this.statusFilteredLoads;
    const deliveredLoads = loads.filter((l) => l.status === "delivered");
    const totalCuFt = loads.reduce((sum, l) => sum + l.totalCubicFeet, 0);
    // What ARRIVED, not what shipped. A pallet corrected to 4-of-5 reduces this,
    // which keeps it consistent with the job's Delivered column on the
    // Production page — the same word must not mean two different things on two
    // screens.
    const deliveredCuFt = deliveredLoads.reduce(
      (sum, l) => sum + this.deliveredCubicFeetForLoad(l),
      0
    );
    // Invoiced totals use the effective amount (manual override when set).
    const totalInvoiced = loads.reduce(
      (sum, l) => sum + this.effectiveInvoiceAmount(l),
      0
    );
    // Deliberately still the INVOICED amount, not scaled down by short
    // deliveries. Creede's damaged pieces get remade — "they're screwed up and
    // we've got to remake them" — so the customer still owes the full amount and
    // cutting this would under-report what is owed. Whether a short delivery
    // should ever reduce the invoice is a billing decision, like the estimate-
    // vs-actual basis, and it is his to make rather than ours.
    const invoicedDelivered = deliveredLoads.reduce(
      (sum, l) => sum + this.effectiveInvoiceAmount(l),
      0
    );

    return {
      totalLoads: loads.length,
      deliveredLoads: deliveredLoads.length,
      inTransitLoads: loads.filter((l) => l.status === "in_transit").length,
      loadedLoads: loads.filter((l) => l.status === "loaded").length,
      plannedLoads: loads.filter((l) => l.status === "planned").length,
      storedLoads: loads.filter((l) => l.status === "stored").length,
      totalCuFt: Math.round(totalCuFt * 100) / 100,
      deliveredCuFt: Math.round(deliveredCuFt * 100) / 100,
      totalInvoiced: Math.round(totalInvoiced * 100) / 100,
      invoicedDelivered: Math.round(invoicedDelivered * 100) / 100,
      overriddenLoads: loads.filter((l) => l.invoiceOverride != null).length,
      totalPallets: loads.reduce((sum, l) => sum + l.pallets.length, 0),
    };
  }

  // Creede, 2026-08-14: planned -> delivered "should flip automatically once the
  // ship date passes... What I wouldn't necessarily want to do is come in here
  // and edit the status every day."
  //
  // DERIVED, not written, and deliberately not shown as "Delivered". Loads slip
  // constantly — "we have loads get pushed back all the time. Customers fail to
  // pick it up" — so a past ship date is evidence that a load is DUE, not that
  // it arrived. Two reasons not to assert delivery here:
  //
  //   - Writing `delivered` on a timer records something nobody observed, on
  //     the exact field the invoice totals and the job's delivered counts key
  //     off.
  //   - Merely DISPLAYING `delivered` while the stored status stays `planned`
  //     would show a delivered load against a job showing zero delivered
  //     pieces, because syncDeliveredCountsForLoad keys on the stored status.
  //     That is precisely the screen-vs-job contradiction Creede reported.
  //
  // So this marks loads as awaiting confirmation and lets a human answer. The
  // "you had a load yesterday. Did it deliver?" prompt consumes this list.
  isAwaitingDeliveryConfirmation = (load: MMLoad): boolean =>
    isAwaitingDeliveryConfirmation(load);

  // Every load whose ship date has passed with no answer yet, oldest first, so
  // the longest-outstanding question is asked first.
  //
  // Local time, not toISOString(): the latter is UTC, which in US Central rolls
  // over to tomorrow in the early evening and would start asking about today's
  // loads before the day is out.
  get loadsAwaitingDeliveryConfirmation(): MMLoad[] {
    return this.loads
      .filter((load) => this.isAwaitingDeliveryConfirmation(load))
      .sort((a, b) => a.shipDate.localeCompare(b.shipDate));
  }

  // All loads (across every project) grouped by ship date. Keys are plain
  // yyyy-MM-dd strings (shipDate is already normalized via toDateOnly on
  // read); loads with no ship date are ignored. Feeds the Shipping Calendar.
  get loadsByShipDate(): Map<string, MMLoad[]> {
    const byDate = new Map<string, MMLoad[]>();
    this.loads.forEach((load) => {
      if (!load.shipDate) return;
      const existing = byDate.get(load.shipDate);
      if (existing) {
        existing.push(load);
      } else {
        byDate.set(load.shipDate, [load]);
      }
    });
    return byDate;
  }

  // ========================================
  // ACTIONS - SELECTION
  // ========================================

  setSelectedProjectId = (id: string | null) => {
    this.selectedProjectId = id;
    this.selectedLoadId = null;
  };

  setSelectedLoadId = (id: string | null) => {
    this.selectedLoadId = id;
  };

  setFilterByStatus = (status: LoadStatus | "all") => {
    this.filterByStatus = status;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  // ========================================
  // ACTIONS - LOADS
  // ========================================

  createLoad = async (
    data: Omit<
      MMLoad,
      "id" | "pallets" | "freeRackItems" | "totalCubicFeet" | "invoiceAmount"
    >
  ): Promise<MMLoad> => {
    try {
      const createData: Record<string, unknown> = {
        projectId: data.projectId,
        loadNumber: data.loadNumber,
        shipDate: toDateOnly(data.shipDate),
        carrier: data.carrier,
        isOversized: data.isOversized,
        totalCubicFeet: 0,
        invoiceAmount: 0,
        status: data.status,
      };
      if (data.contactName) createData.contactName = data.contactName;
      if (data.contactPhone) createData.contactPhone = data.contactPhone;
      // != null (not truthy) — a 0 override must be sent.
      if (data.invoiceOverride != null) {
        createData.invoiceOverride = data.invoiceOverride;
      }
      // Only the dates that are set: an API a deploy behind the migration
      // would refuse the columns, and a new load usually has none.
      for (const field of LOAD_EVENT_DATE_FIELDS) {
        if (data[field]) createData[field] = toDateOnly(data[field]);
      }

      const result: any = await this.loadsService.create(createData);

      const newLoad: MMLoad = {
        // A new load starts with nothing on it — no pallets, no free rack.
        freeRackItems: [],
        id: result.loadId,
        projectId: result.projectId,
        loadNumber: result.loadNumber,
        shipDate: toDateOnly(result.shipDate),
        carrier: result.carrier,
        isOversized: !!result.isOversized,
        contactName: result.contactName || undefined,
        contactPhone: result.contactPhone || undefined,
        pallets: [],
        totalCubicFeet: 0,
        invoiceAmount: 0,
        invoiceOverride:
          result.invoiceOverride != null
            ? Number(result.invoiceOverride)
            : undefined,
        status: result.status || "planned",
        storedDate: toDateOnly(result.storedDate) || null,
        deliveredDate: toDateOnly(result.deliveredDate) || null,
        billedDate: toDateOnly(result.billedDate) || null,
        paidDate: toDateOnly(result.paidDate) || null,
      };

      runInAction(() => {
        this.loads.push(newLoad);
      });
      return newLoad;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to create load";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to create load"),
        options: { variant: "error" },
      });
      throw error;
    }
  };

  updateLoad = async (
    id: string,
    data: Partial<Omit<MMLoad, "id" | "pallets">>
  ): Promise<boolean> => {
    try {
      // Normalize to yyyy-MM-dd — the API rejects a full datetime for the
      // `format: 'date'` shipDate, which otherwise aborts the whole edit.
      const patchData: Partial<Omit<MMLoad, "id" | "pallets">> = { ...data };
      if (data.shipDate != null) {
        patchData.shipDate = toDateOnly(data.shipDate);
      }
      // Event dates (item 37): "" or null clears one.
      for (const field of LOAD_EVENT_DATE_FIELDS) {
        if (field in data) patchData[field] = data[field] ? toDateOnly(data[field]) : null;
      }

      await this.loadsService.patch(id, patchData);

      runInAction(() => {
        const index = this.loads.findIndex((l) => l.id === id);
        if (index !== -1) {
          this.loads[index] = { ...this.loads[index], ...patchData };
        }
      });

      // Status drives the job's delivered counts, so re-derive them whenever it
      // moves — in or out of `delivered`. Runs after the local merge above so
      // the recompute sees the status we just saved.
      if (data.status !== undefined) {
        await this.syncDeliveredCountsForLoad(id);
      }
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update load";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update load"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  updateLoadStatus = async (
    id: string,
    status: LoadStatus
  ): Promise<boolean> => {
    return this.updateLoad(id, { status });
  };

  // Item 12 release guard. Paired VDT/precast jobs stay separate everywhere
  // except shipping, where they must leave together ("the system's going to
  // kind of block you… if you haven't taken everything and shipped it").
  // Returns a confirm-style warning message when `nextStatus` would mark the
  // LAST load of `projectId` as delivered while its paired sibling job still
  // has undelivered pieces, or null when no warning applies.
  //
  // Now that one truck carries both halves, the guard can answer the question
  // directly instead of guessing at it. It compares the sibling's PIECES
  // against what has actually been committed to a truck anywhere in the pair,
  // so a half whose pieces all ride on this job's loads reads as shipped —
  // which under the old per-project heuristic ("the sibling has no loads of
  // its own") was the exact shape that produced a false warning every time.
  //
  // This is a WARN-and-confirm, not a hard block: callers show the message
  // via window.confirm (matching the delete flows) and proceed only on OK.
  pairedReleaseWarning = (
    nextStatus: LoadStatus,
    projectId: string,
    changingLoadId?: string
  ): string | null => {
    if (nextStatus !== "delivered") return null;

    if (changingLoadId) {
      const load = this.getLoadById(changingLoadId);
      // Unknown load, or already delivered (no real transition) — no warning.
      if (!load || load.status === "delivered") return null;
    }

    const projectStore = this.rootStore.mmProjectStore;
    const pairedId = projectStore?.getProjectById(projectId)?.pairedProjectId;
    if (!pairedId || pairedId === projectId) return null;
    const paired = projectStore?.getProjectById(pairedId);
    if (!paired) return null;

    // Only the LAST undelivered load in the pair triggers the guard — any
    // other truck still out means this is not the moment of no return.
    const otherLoads = this.getLoadsInScope(projectId).filter(
      (l) => l.id !== changingLoadId
    );
    if (otherLoads.some((l) => l.status !== "delivered")) return null;

    // What the sibling still has that is on no truck at all. Counted from its
    // marks rather than from its loads, so pieces riding on THIS job's loads
    // are correctly seen as shipped.
    const siblingMarks =
      this.productionStore?.getProductionItemsByProject(pairedId) ?? [];
    if (siblingMarks.length === 0) return null;

    const committed = new Map<string, number>();
    for (const load of this.getLoadsInScope(projectId)) {
      for (const item of this.allItemsOnLoad(load)) {
        if (this.itemProjectId(load, item) !== pairedId) continue;
        const key = item.markNumber?.trim().toUpperCase();
        if (!key) continue;
        committed.set(key, (committed.get(key) ?? 0) + (item.quantity || 0));
      }
    }

    // Aggregate the sibling's marks by number first: the committed tally is
    // keyed by mark number, so two production items sharing one number would
    // otherwise each subtract the same committed pieces.
    const wanted = new Map<string, number>();
    for (const mark of siblingMarks) {
      const key = mark.markNumber.trim().toUpperCase();
      if (!key) continue;
      wanted.set(key, (wanted.get(key) ?? 0) + mark.quantity);
    }

    let short = 0;
    wanted.forEach((quantity, key) => {
      const left = quantity - (committed.get(key) ?? 0);
      if (left > 0) short += left;
    });
    if (short <= 0) return null;

    const pieces = short === 1 ? "piece" : "pieces";
    return `Paired job #${paired.jobNumber} still has ${short} ${pieces} on no load — release anyway?`;
  };

  // Set or clear the manual billed amount. null clears the override so the
  // auto-calculated invoiceAmount is billed again; 0 is a real override.
  setInvoiceOverride = async (
    id: string,
    value: number | null
  ): Promise<boolean> => {
    try {
      await this.loadsService.patch(id, { invoiceOverride: value });

      runInAction(() => {
        const load = this.loads.find((l) => l.id === id);
        if (load) {
          load.invoiceOverride = value ?? undefined;
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update billed amount";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update billed amount"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteLoad = async (id: string): Promise<boolean> => {
    try {
      // Pallets will be cascade-deleted by the database FK
      await this.loadsService.remove(id);

      runInAction(() => {
        const index = this.loads.findIndex((l) => l.id === id);
        if (index !== -1) {
          this.loads.splice(index, 1);
        }
        if (this.selectedLoadId === id) {
          this.selectedLoadId = null;
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to delete load";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete load"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // ACTIONS - PALLETS
  // ========================================

  addPallet = async (
    loadId: string,
    items: MMPalletItem[]
  ): Promise<MMPallet | null> => {
    const load = this.getLoadById(loadId);
    if (!load) return null;

    items = sortedItems(items);
    const palletCuFt = items.reduce((sum, i) => sum + i.cubicFeet, 0);
    const nextPalletNumber = this.nextPalletNumber(load.projectId);

    try {
      const result: any = await this.palletsService.create({
        loadId,
        palletNumber: nextPalletNumber,
        items: JSON.stringify(items),
        totalCubicFeet: Math.round(palletCuFt * 100) / 100,
      });

      const newPallet: MMPallet = {
        id: result.palletId,
        loadId,
        palletNumber: nextPalletNumber,
        items,
        totalCubicFeet: Math.round(palletCuFt * 100) / 100,
      };

      runInAction(() => {
        load.pallets.push(newPallet);
      });

      // Totals are cubic feet and weight; the JOB's delivered piece counts are
      // a different sum and were never re-derived here. Creede walked out to
      // find the difference: "somebody's like, hey, there's two of these pieces
      // on a pallet. And then I walk out there in the yard and there's three."
      // Counts follow contents, on every path that changes a pallet.
      await this.recalculateAndPersistLoadTotals(loadId);
      await this.syncDeliveredCountsForLoad(loadId);

      return newPallet;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to add pallet";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to add pallet"),
        options: { variant: "error" },
      });
      return null;
    }
  };

  updatePallet = async (
    loadId: string,
    palletId: string,
    items: MMPalletItem[]
  ): Promise<boolean> => {
    const load = this.getLoadById(loadId);
    if (!load) return false;

    const palletIndex = load.pallets.findIndex((p) => p.id === palletId);
    if (palletIndex === -1) return false;

    // Captured BEFORE the write: an edit that drops a mark from the pallet
    // entirely still has to walk that mark's delivered count back.
    const markNumbersBefore = this.markNumbersOnLoad(load);

    items = sortedItems(items);
    const palletCuFt = items.reduce((sum, i) => sum + i.cubicFeet, 0);

    try {
      await this.palletsService.patch(palletId, {
        items: JSON.stringify(items),
        totalCubicFeet: Math.round(palletCuFt * 100) / 100,
      });

      runInAction(() => {
        load.pallets[palletIndex] = {
          ...load.pallets[palletIndex],
          items,
          totalCubicFeet: Math.round(palletCuFt * 100) / 100,
        };
      });

      // Totals are cubic feet and weight; the JOB's delivered piece counts are
      // a different sum and were never re-derived here. Creede walked out to
      // find the difference: "somebody's like, hey, there's two of these pieces
      // on a pallet. And then I walk out there in the yard and there's three."
      // Counts follow contents, on every path that changes a pallet.
      await this.recalculateAndPersistLoadTotals(loadId);
      await this.syncDeliveredCountsForLoad(loadId, markNumbersBefore);

      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update pallet";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update pallet"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // Replace a load's free rack wholesale. Same contract as `updatePallet`: the
  // caller submits the COMPLETE array, not a delta, because the column is a
  // single JSON blob. An empty array clears it — sent as an explicit null,
  // which is what the schema's Type.Null() arm exists for.
  updateFreeRack = async (
    loadId: string,
    items: MMPalletItem[]
  ): Promise<boolean> => {
    const load = this.getLoadById(loadId);
    if (!load) return false;

    // Captured BEFORE the write, as updatePallet does: a mark taken off the
    // rack entirely still has to have its delivered count walked back.
    const markNumbersBefore = this.markNumbersOnLoad(load);

    try {
      await this.loadsService.patch(loadId, {
        freeRackItems: items.length > 0 ? JSON.stringify(items) : null,
      });

      runInAction(() => {
        load.freeRackItems = items;
      });

      // Recalculate and persist load totals — free rack counts toward both.
      await this.recalculateAndPersistLoadTotals(loadId);
      // Counts follow contents on the free rack too. deliveredQuantityForMark
      // counts these rows, so a rack edited on a delivered load left the job's
      // delivered pieces at whatever they were before the edit.
      await this.syncDeliveredCountsForLoad(loadId, markNumbersBefore);

      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to update free rack";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update free rack"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // STAGED PALLETS (2026-10-16 item 10)
  // ========================================

  // The job's staged pallets, across the pair like its loads.
  stagedPalletsFor = (projectId: string): MMStagedPallet[] => {
    const scope = this.projectScope(projectId);
    return this.stagedPallets.filter((p) => scope.includes(p.projectId));
  };

  // S-numbers run on and are never reused, counting pallets that have since
  // gone onto a load: "S3" on a slip must mean one pallet.
  nextStagedNumber = (projectId: string): number => {
    const scope = this.projectScope(projectId);
    const numbers = [
      ...this.stagedPalletsFor(projectId).map((p) => p.stagedNumber),
      ...this.loads
        .filter((l) => scope.includes(l.projectId))
        .flatMap((l) => l.pallets.map((p) => p.stagedNumber ?? 0)),
    ];
    return numbers.length > 0 ? Math.max(0, ...numbers) + 1 : 1;
  };

  private notifyFailure = (error: unknown, fallback: string) => {
    runInAction(() => {
      this.error = error instanceof Error ? error.message : fallback;
    });
    this.rootStore.notificationStore?.addNotification({
      message: mmErrorMessage(error, fallback),
      options: { variant: "error" },
    });
  };

  addStagedPallet = async (
    projectId: string,
    items: MMPalletItem[]
  ): Promise<MMStagedPallet | null> => {
    items = sortedItems(items);
    const totalCubicFeet = Math.round(items.reduce((s, i) => s + i.cubicFeet, 0) * 100) / 100;
    const stagedNumber = this.nextStagedNumber(projectId);
    try {
      const result = (await this.palletsService.create({
        loadId: null,
        palletNumber: null,
        projectId,
        stagedNumber,
        items: JSON.stringify(items),
        totalCubicFeet,
      })) as { palletId: string };
      const pallet: MMStagedPallet = {
        id: result.palletId,
        projectId,
        stagedNumber,
        items,
        totalCubicFeet,
      };
      runInAction(() => {
        this.stagedPallets.push(pallet);
      });
      return pallet;
    } catch (error) {
      this.notifyFailure(error, "Failed to add staged pallet");
      return null;
    }
  };

  updateStagedPallet = async (palletId: string, items: MMPalletItem[]): Promise<boolean> => {
    const pallet = this.stagedPallets.find((p) => p.id === palletId);
    if (!pallet) return false;
    items = sortedItems(items);
    const totalCubicFeet = Math.round(items.reduce((s, i) => s + i.cubicFeet, 0) * 100) / 100;
    try {
      await this.palletsService.patch(palletId, { items: JSON.stringify(items), totalCubicFeet });
      runInAction(() => {
        pallet.items = items;
        pallet.totalCubicFeet = totalCubicFeet;
      });
      return true;
    } catch (error) {
      this.notifyFailure(error, "Failed to update staged pallet");
      return false;
    }
  };

  removeStagedPallet = async (palletId: string): Promise<boolean> => {
    try {
      await this.palletsService.remove(palletId);
      runInAction(() => {
        this.stagedPallets = this.stagedPallets.filter((p) => p.id !== palletId);
      });
      return true;
    } catch (error) {
      this.notifyFailure(error, "Failed to remove staged pallet");
      return false;
    }
  };

  // Onto a truck: it takes the job's next pallet number and keeps its S-number,
  // so the slip can say which staged pallet it was. One row, one write -- it
  // cannot end up half on the load.
  assignStagedPallet = async (palletId: string, loadId: string): Promise<boolean> => {
    const staged = this.stagedPallets.find((p) => p.id === palletId);
    const load = this.getLoadById(loadId);
    if (!staged || !load) return false;
    const palletNumber = this.nextPalletNumber(load.projectId);
    const markNumbersBefore = this.markNumbersOnLoad(load);
    try {
      await this.palletsService.patch(palletId, { loadId, palletNumber });
      runInAction(() => {
        this.stagedPallets = this.stagedPallets.filter((p) => p.id !== palletId);
        load.pallets.push({
          id: staged.id,
          loadId,
          palletNumber,
          items: staged.items,
          totalCubicFeet: staged.totalCubicFeet,
          stagedNumber: staged.stagedNumber,
        });
      });
      await this.recalculateAndPersistLoadTotals(loadId);
      await this.syncDeliveredCountsForLoad(loadId, markNumbersBefore);
      return true;
    } catch (error) {
      this.notifyFailure(error, "Failed to put the staged pallet on the load");
      return false;
    }
  };

  // Pieces of a mark sitting on the job's staged pallets. They are committed --
  // on a pallet, labelled -- so the over-delivery warning counts them.
  stagedQuantityForMark = (projectId: string, markNumber: string, excludePalletId?: string): number => {
    const target = markNumber.trim().toUpperCase();
    return this.stagedPalletsFor(projectId)
      .filter((p) => p.id !== excludePalletId)
      .flatMap((p) => p.items)
      .reduce(
        (sum, item) =>
          item.markNumber.trim().toUpperCase() === target ? sum + (item.quantity || 0) : sum,
        0
      );
  };

  // Pieces of a mark on ANY pallet of the job -- every load plus staged. What
  // the staged builder's "N left" counts against.
  palletisedQuantityForMark = (projectId: string, markNumber: string): number =>
    this.loadedQuantityForMark(projectId, markNumber) + this.stagedQuantityForMark(projectId, markNumber);

  // Which pallets close up when one comes off an UNDELIVERED load (2026-10-16
  // item 38). Creede, 10-23: "If a pallet is assigned to a load that has not
  // yet been delivered, I think the numbering should shift accordingly." And
  // 10-27, after Dutsch: "Shift pallet numbers up on undelivered loads."
  //
  // Only pallets on the job's undelivered loads (planned, loaded, in transit)
  // move. Delivered and stored loads keep theirs -- those slips are out of the
  // yard -- and so do staged pallets, which have no number. Numbers are
  // job-wide, so a delivered pallet can sit between undelivered ones: the gap
  // closes by giving each later undelivered pallet the lowest free number
  // above the one removed, SKIPPING numbers a delivered or stored pallet holds,
  // so no two pallets ever end up sharing a label. Pure: nothing is written.
  palletRenumberingAfterRemoving = (
    loadId: string,
    palletId: string
  ): { id: string; loadId: string; from: number; to: number }[] => {
    const load = this.getLoadById(loadId);
    const removed = load?.pallets.find((p) => p.id === palletId);
    if (!load || !removed || !UNDELIVERED_STATUSES.includes(load.status)) return [];

    const loads = this.getLoadsInScope(load.projectId);
    const fixed = new Set(
      loads
        .filter((l) => !UNDELIVERED_STATUSES.includes(l.status))
        .flatMap((l) => l.pallets.map((p) => p.palletNumber))
    );
    const later = loads
      .filter((l) => UNDELIVERED_STATUSES.includes(l.status))
      .flatMap((l) => l.pallets.map((p) => ({ id: p.id, loadId: l.id, number: p.palletNumber })))
      .filter((p) => p.id !== palletId && p.number > removed.palletNumber)
      .sort((a, b) => a.number - b.number);

    const moves: { id: string; loadId: string; from: number; to: number }[] = [];
    let next = removed.palletNumber;
    for (const pallet of later) {
      while (fixed.has(next)) next += 1;
      if (next < pallet.number) {
        moves.push({ id: pallet.id, loadId: pallet.loadId, from: pallet.number, to: next });
        next += 1;
      } else {
        next = pallet.number + 1;
      }
    }
    return moves;
  };

  removePallet = async (
    loadId: string,
    palletId: string
  ): Promise<boolean> => {
    const load = this.getLoadById(loadId);
    if (!load) return false;

    const palletIndex = load.pallets.findIndex((p) => p.id === palletId);
    if (palletIndex === -1) return false;

    // Captured BEFORE the write, for the same reason as an edit: these marks
    // are about to stop being on this load, and their counts must follow.
    const markNumbersBefore = this.markNumbersOnLoad(load);
    // Worked out before the delete, from the pallets as they stand.
    const renumbering = this.palletRenumberingAfterRemoving(loadId, palletId);

    try {
      await this.palletsService.remove(palletId);

      runInAction(() => {
        load.pallets.splice(palletIndex, 1);
      });

      // Close the gap on undelivered loads (item 38). The delete has already
      // happened, so a failed renumber is reported, not rolled back: the
      // numbers stay as they were, which is the old, gap-leaving behaviour.
      if (renumbering.length > 0) {
        try {
          await Promise.all(
            renumbering.map((move) => this.palletsService.patch(move.id, { palletNumber: move.to }))
          );
          runInAction(() => {
            for (const move of renumbering) {
              const pallet = this.getLoadById(move.loadId)?.pallets.find((p) => p.id === move.id);
              if (pallet) pallet.palletNumber = move.to;
            }
            for (const touched of new Set(renumbering.map((m) => m.loadId))) {
              this.getLoadById(touched)?.pallets.sort((a, b) => a.palletNumber - b.palletNumber);
            }
          });
        } catch (error) {
          this.rootStore.notificationStore?.addNotification({
            message: mmErrorMessage(
              error,
              "The pallet was deleted, but the later pallets could not be renumbered — refresh and check their numbers"
            ),
            options: { variant: "error" },
          });
        }
      }

      // Deleting a pallet takes its pieces off the job as surely as editing one
      // does, so the counts are re-derived here too.
      await this.recalculateAndPersistLoadTotals(loadId);
      await this.syncDeliveredCountsForLoad(loadId, markNumbersBefore);

      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to remove pallet";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to remove pallet"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // HELPER - RECALCULATE
  // ========================================

  // ========================================
  // BILLING GUARDRAILS
  // ========================================

  private get projectStore() {
    return this.rootStore.mmProjectStore;
  }

  private get productionStore() {
    return this.rootStore.mmProductionStore;
  }

  // The project's $/cu.ft, or null when the job has not been priced. Zero is
  // treated as absent on purpose: `calculateBillingRate` returns 0 when total
  // cubic feet is 0, which is "not priced yet", not "free".
  private billingRateFor = (projectId: string): number | null => {
    return this.projectStore?.getProjectById(projectId)?.billingRatePerCuFt || null;
  };

  private priceCubicFeet = (projectId: string, cubicFeet: number): number => {
    const rate = this.billingRateFor(projectId) ?? 0;
    return Math.round(cubicFeet * rate * 100) / 100;
  };

  // Re-price every load on a project against the project's CURRENT rate.
  //
  // `recalculateAndPersistLoadTotals` is the only writer of `invoiceAmount`
  // and it fires only on pallet add/update/remove, so two things were true and
  // both were silent. Editing a project's contract amount or cubic feet moved
  // `billingRatePerCuFt` and left every existing load priced at the old rate.
  // And a load whose pallets were built before the contract was entered priced
  // at $0 and stayed there permanently, because the rate was absent at the one
  // moment anything looked. Neither surfaced as an error — they just produced
  // confident wrong money, which is also what would have made the new
  // contract-ceiling warning under-report exactly when it matters.
  //
  // Never touches `invoiceOverride`: that is a deliberate manual billing
  // decision and outranks the calculated figure.
  repriceLoadsForProject = async (projectId: string): Promise<number> => {
    const rate = this.billingRateFor(projectId);
    if (rate == null) return 0;

    // Scoped, not per-project: a load carrying both halves has to be re-priced
    // when EITHER half's rate changes, and it is only reachable from one of
    // them. `computedInvoiceFor` re-prices the whole truck at both rates, so
    // running this from either half converges on the same figure.
    //
    // Never a load with a BILLED DATE (item 37): that invoice has gone out, and
    // a rate change -- or the mock-up rate arriving (item 22) -- must not
    // rewrite what was billed.
    const stale = this.getLoadsInScope(projectId).filter(
      (load) => !load.billedDate && this.computedInvoiceFor(load) !== load.invoiceAmount
    );
    if (stale.length === 0) return 0;

    let repriced = 0;
    await Promise.all(
      stale.map(async (load) => {
        const invoiceAmount = this.computedInvoiceFor(load);
        try {
          await this.loadsService.patch(load.id, { invoiceAmount });
          runInAction(() => {
            load.invoiceAmount = invoiceAmount;
          });
          repriced += 1;
        } catch (error) {
          console.error("Failed to re-price load", load.loadNumber, error);
        }
      })
    );

    if (repriced < stale.length) {
      this.rootStore.notificationStore?.addNotification({
        message: `Re-priced ${repriced} of ${stale.length} loads for the new rate — reopen the job to retry the rest.`,
        options: { variant: "warning" },
      });
    }
    return repriced;
  };

  // Cumulative billed-to-date against the contract amount. Creede: "we'd try
  // and bill $30,000 and we can't do that. That's more than the contract
  // amount… there would need to be a warning."
  //
  // Compares ALL loads rather than delivered ones: the point is to catch it
  // BEFORE delivery, which is the only moment it is still cheap to fix.
  // Returns null when the job has no contract to compare against, so an
  // unpriced job shows nothing rather than a warning about zero.
  overContractWarning = (
    projectId: string
  ): { billed: number; contract: number; overBy: number } | null => {
    const project = this.projectStore?.getProjectById(projectId);
    if (!project || !project.contractAmount) return null;

    // Every truck the pair has shipped, but only THIS job's share of each one.
    // A shared load bills both contracts, and neither ceiling may be measured
    // against the other's pieces. The same figures as Administration's Billing
    // tab -- billed plus still to bill -- so the two can never disagree.
    const { billedTotal, pendingTotal } = this.billingFor(projectId);
    const billed = billedTotal + pendingTotal;
    if (billed <= project.contractAmount) return null;

    return {
      billed: Math.round(billed * 100) / 100,
      contract: project.contractAmount,
      overBy: Math.round((billed - project.contractAmount) * 100) / 100,
    };
  };

  // Administration -> Billing (2026-10-16 item 7). Creede: "pull from our loads
  // when we have our billing amount on there… a log of all the times we've
  // billed… What percentage of the job have we billed? amount billed, amount
  // remaining." Each load the job has a share of, oldest first, with THIS
  // job's share of the money.
  billingFor = (
    projectId: string
  ): { lines: BillingLine[]; billedTotal: number; pendingTotal: number; paidTotal: number } => {
    const lines = this.getLoadsInScope(projectId)
      .map((load) => ({
        load,
        amount: this.invoiceShareForProject(load, projectId),
        cubicFeet: this.cubicFeetOnLoadForProject(load, projectId),
        billed: !!load.billedDate,
        billable: BILLABLE_STATUSES.includes(load.status),
        paid: !!load.paidDate,
      }))
      // A paired load with none of this job's pieces isn't this job's bill.
      .filter((line) => line.amount > 0 || line.cubicFeet > 0 || line.load.projectId === projectId)
      // By the billed date where there is one (item 37: "the log dates by
      // it"), else the ship date.
      .sort(
        (a, b) =>
          (a.load.billedDate || a.load.shipDate || "9999").localeCompare(
            b.load.billedDate || b.load.shipDate || "9999"
          ) ||
          a.load.loadNumber.localeCompare(b.load.loadNumber, undefined, { numeric: true })
      );
    const sum = (billed: boolean) =>
      Math.round(
        lines.filter((l) => l.billed === billed).reduce((s, l) => s + l.amount, 0) * 100
      ) / 100;
    const paidTotal =
      Math.round(lines.filter((l) => l.billed && l.paid).reduce((s, l) => s + l.amount, 0) * 100) / 100;
    return { lines, billedTotal: sum(true), pendingTotal: sum(false), paidTotal };
  };

  // How many pieces of a mark are already committed to loads on this job.
  // The join is the free-text `markNumber` string — pallet items carry no FK
  // to a production item — which is exactly the failure Creede described:
  // "our guys don't read the tag right on the piece, and our load list says
  // marks that aren't even on the job."
  private loadedQuantityForMark = (
    projectId: string,
    markNumber: string,
    excludeLoadId?: string
  ): number => {
    const target = markNumber.trim().toUpperCase();
    const countIn = (items: MMPalletItem[]): number =>
      items.reduce(
        (sum, item) =>
          item.markNumber.trim().toUpperCase() === target
            ? sum + (item.quantity || 0)
            : sum,
        0
      );
    return this.getLoadsInScope(projectId)
      .filter((load) => load.id !== excludeLoadId)
      .reduce(
        (sum, load) =>
          sum +
          load.pallets.reduce(
            (palletSum, pallet) => palletSum + countIn(pallet.items),
            0
          ) +
          // Free-rack rows are committed pieces too. Omitting them would make
          // the warning under-report by exactly the pieces riding on boards.
          countIn(load.freeRackItems ?? []),
        0
      );
  };

  // Every distinct mark riding on a load, pallets and free rack alike, keyed
  // the same way loadedQuantityForMark keys them so the two agree about what
  // "the same mark" means.
  private markNumbersOnLoad = (load: MMLoad): string[] => {
    const seen = new Set<string>();
    const collect = (items: MMPalletItem[]) => {
      for (const item of items) {
        const key = item.markNumber?.trim().toUpperCase();
        if (key) seen.add(key);
      }
    };
    load.pallets.forEach((pallet) => collect(pallet.items));
    collect(load.freeRackItems ?? []);
    return [...seen];
  };

  // Pieces of a mark that have actually DELIVERED: the same walk as
  // loadedQuantityForMark, narrowed to loads whose status says they arrived.
  private deliveredQuantityForMark = (
    projectId: string,
    markNumber: string
  ): number => {
    const target = markNumber.trim().toUpperCase();
    // `deliveredQuantity ?? quantity`: a pallet with damage recorded contributes
    // what ARRIVED, not what was loaded. `??` and not `||`, because a delivered
    // count of zero — a pallet that arrived broken — is a real answer and must
    // not fall back to the loaded figure.
    const countIn = (items: MMPalletItem[]): number =>
      items.reduce(
        (sum, item) =>
          item.markNumber.trim().toUpperCase() === target
            ? sum + (item.deliveredQuantity ?? item.quantity ?? 0)
            : sum,
        0
      );
    return this.getLoadsInScope(projectId)
      .filter((load) => load.status === "delivered")
      .reduce(
        (sum, load) =>
          sum +
          load.pallets.reduce((p, pallet) => p + countIn(pallet.items), 0) +
          countIn(load.freeRackItems ?? []),
        0
      );
  };

  // Creede marked a load delivered on the 08-14 call and the job did not move.
  // He guessed the pieces were not marked produced; that is not it —
  // piecesProduced and piecesDelivered are independent columns and neither
  // gates the other. The actual cause is that NOTHING on the load side has ever
  // written mmProductionItems.piecesDelivered. mm-loads registers no after
  // hooks at all (`after: { all: [] }`), and the only writer of piecesDelivered
  // is the manual cell on the Production grid.
  //
  // It has to be fixed here rather than server-side: mmPallets.items is an
  // opaque JSON string the API deliberately never parses, so the server cannot
  // do this mark join at all without either breaking that convention or adding
  // a real FK to pallet items.
  //
  // RECOMPUTED, never incremented. The delivered total for a mark is derived
  // from every delivered load on the job, so saving the same load twice, moving
  // a load back out of `delivered`, or editing pallet quantities afterwards all
  // land on the right number instead of double-counting. That also means this
  // is safe to call on ANY status change, not just the transition into
  // delivered.
  //
  // Marks that match nothing on the job are skipped — those are the bad tags
  // Creede described ("our load list says marks that aren't even on the job"),
  // and overDeliveryWarning already surfaces them at pallet-entry time, which
  // is where they can actually be corrected.
  /**
   * Re-derive the job's delivered piece counts from what is on this load.
   *
   * `alsoRecheck` carries mark numbers that WERE on the load before the change
   * being saved. Without it, taking a mark off a load never corrects its count:
   * the walk below only visits marks currently on the load, so a deleted pallet
   * leaves its pieces counted as delivered forever -- the screen saying three
   * where the yard has none, which is the same class of mismatch Creede
   * reported from the other direction.
   */
  syncDeliveredCountsForLoad = async (
    loadId: string,
    alsoRecheck: string[] = []
  ): Promise<void> => {
    const load = this.getLoadById(loadId);
    const productionStore = this.productionStore;
    if (!load || !productionStore) return;

    try {
      // A shared load delivers both halves at once, so the mark lookup has to
      // reach the paired job or its pieces would never be counted delivered.
      const marks = this.projectScope(load.projectId).flatMap((id) =>
        productionStore.getProductionItemsByProject(id)
      );
      const byNumber = new Map(
        marks.map((mark) => [mark.markNumber.trim().toUpperCase(), mark])
      );

      const toCheck = new Set([
        ...this.markNumbersOnLoad(load),
        ...alsoRecheck.map((m) => m.trim().toUpperCase()),
      ]);

      for (const markNumber of toCheck) {
        const mark = byNumber.get(markNumber);
        if (!mark) continue;

        const delivered = this.deliveredQuantityForMark(
          load.projectId,
          markNumber
        );
        // Only write real changes. A load of twelve marks would otherwise fire
        // twelve patches on every save, and the API rate limiter (100/min) is
        // already close to saturated by a single app boot.
        if (delivered === mark.piecesDelivered) continue;

        await productionStore.updateDeliveredCount(mark.id, delivered);
      }
    } catch (error) {
      // A propagation failure must not make a saved load look unsaved — the
      // load itself is already persisted by the time we get here.
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(
          error,
          "Load saved, but the job's delivered counts could not be updated"
        ),
        options: { variant: "warning" },
      });
    }

    // Item 6: a finished job hands its schedule colour back to the pool, which
    // is what keeps sixteen shades sufficient. This is the moment to check —
    // delivered counts have just been recomputed, and a job being finished is
    // exactly "its last piece arrived".
    //
    // Both halves of a paired job are checked, because a shared truck delivers
    // both at once (item 12) and each keeps its own colour.
    for (const projectId of this.projectScope(load.projectId)) {
      void this.releaseColorIfDelivered(projectId);
    }
  };

  // A job is finished when every piece on it has been delivered.
  //
  // Derived rather than read off a status field because there ISN'T one: a
  // project's detailingStatus runs new -> ... -> approved -> plant, and `plant`
  // means it is IN production. There is no terminal state on the record, so
  // inventing one would mean asking Creede to maintain a flag by hand.
  //
  // A job with no marks is not "finished", it is unstarted — hence the length
  // check, without which every empty job would immediately release a colour it
  // was about to need.
  private releaseColorIfDelivered = async (projectId: string): Promise<void> => {
    const projectStore = this.projectStore;
    if (!projectStore?.getProjectById(projectId)?.scheduleColor) return;
    if (!this.productionStore?.isProjectFullyDelivered(projectId)) return;

    await projectStore.releaseScheduleColor(projectId);
  };

  // Everything of `markNumber` already committed to ONE load, counted from the
  // freshest source the caller has.
  //
  // `overDeliveryWarning`'s `excludeLoadId` drops the whole load from the
  // persisted tally -- pallets AND free rack, see loadedQuantityForMark -- so
  // the caller has to add all of it back from local state. Doing that by hand
  // at each call site is what produced the bug Creede reproduced: nine A05s
  // saved on pallet one, a tenth added on pallet two, no warning, because the
  // new-pallet path added back only the pallet being built.
  //
  // Lives here rather than in the dialog because it is load arithmetic, and
  // because a shared implementation is the only thing that stops the three
  // call sites drifting apart again.
  committedOnLoad = (
    load: MMLoad,
    markNumber: string,
    options?: { excludePalletId?: string; freeRackOverride?: MMPalletItem[] }
  ): number => {
    const target = markNumber.trim().toUpperCase();
    const countIn = (items: MMPalletItem[]): number =>
      items.reduce(
        (sum, item) =>
          item.markNumber.trim().toUpperCase() === target
            ? sum + (item.quantity || 0)
            : sum,
        0
      );
    const onPallets = (load.pallets ?? [])
      .filter((pallet) => pallet.id !== options?.excludePalletId)
      .reduce((sum, pallet) => sum + countIn(pallet.items), 0);
    // The override is an in-progress edit, which supersedes what is saved.
    const onFreeRack = countIn(
      options?.freeRackOverride ?? load.freeRackItems ?? []
    );
    return onPallets + onFreeRack;
  };

  // A warning, never a block — Creede: "It doesn't necessarily need to stop him
  // from doing that, but there definitely be [a warning]." Two distinct cases,
  // and the second one is arguably the more valuable:
  //   - more pieces committed than the job has, cumulative across every load;
  //   - a mark that is not on this job at all.
  //
  // `stagedQuantity` is what the user has added to the pallet they are building
  // but has not saved yet; without it the count under-reports by exactly what
  // they are typing.
  overDeliveryWarning = (
    projectId: string,
    markNumber: string,
    addingQuantity: number,
    options?: { excludeLoadId?: string; stagedQuantity?: number; excludeStagedPalletId?: string }
  ): string | null => {
    const trimmed = markNumber.trim();
    if (!trimmed) return null;

    const target = trimmed.toUpperCase();
    // Item 12: a shared truck may legitimately carry the paired half's marks,
    // so "not a mark on this job" has to mean "on neither half" — otherwise the
    // warning fires on every correctly-loaded VDT piece.
    const scope = this.projectScope(projectId);
    const items = scope.flatMap(
      (id) => this.productionStore?.getProductionItemsByProject(id) ?? []
    );
    const onJob = items.filter(
      (item) => item.markNumber.trim().toUpperCase() === target
    );

    if (onJob.length === 0) {
      const label = scope.length > 1 ? "either job" : "this job";
      return `${trimmed} is not a mark on ${label} — check the tag on the piece before loading it.`;
    }

    const jobQuantity = onJob.reduce((sum, item) => sum + item.quantity, 0);
    // Staged pallets are committed pieces too (2026-10-16); the one being
    // edited is left out, as the load being edited is.
    const committed =
      this.loadedQuantityForMark(projectId, trimmed, options?.excludeLoadId) +
      this.stagedQuantityForMark(projectId, trimmed, options?.excludeStagedPalletId) +
      (options?.stagedQuantity ?? 0) +
      addingQuantity;

    if (committed <= jobQuantity) return null;
    return `${trimmed}: ${committed} pieces would be on loads but the job only has ${jobQuantity}.`;
  };

  private recalculateAndPersistLoadTotals = async (loadId: string) => {
    const load = this.getLoadById(loadId);
    if (!load) return;

    // Free-rack pieces are on the trailer and on the invoice exactly like
    // palletised ones — they just aren't on a pallet. Leaving them out of the
    // total would under-bill every load carrying a 10-ft column.
    const totalCuFt =
      load.pallets.reduce((sum, p) => sum + p.totalCubicFeet, 0) +
      (load.freeRackItems ?? []).reduce((sum, i) => sum + (i.cubicFeet || 0), 0);
    const roundedCuFt = Math.round(totalCuFt * 100) / 100;

    // Priced per job at each job's own rate (item 12). Identical to the old
    // single-rate figure for any load whose pieces all belong to one job.
    const invoiceAmount = this.computedInvoiceFor(load);

    try {
      // Keeps invoiceAmount (the auto cubic-foot reference) up to date on
      // every pallet change, but must NEVER write or clear invoiceOverride —
      // that's a manual billing decision (item 11).
      await this.loadsService.patch(loadId, {
        totalCubicFeet: roundedCuFt,
        invoiceAmount,
      });

      runInAction(() => {
        load.totalCubicFeet = roundedCuFt;
        load.invoiceAmount = invoiceAmount;
      });
    } catch (error) {
      // Still update locally even if API fails
      runInAction(() => {
        load.totalCubicFeet = roundedCuFt;
        load.invoiceAmount = invoiceAmount;
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to save load totals"),
        options: { variant: "error" },
      });
      console.error("Failed to persist load totals:", error);
    }
  };

  // ========================================
  // RESET
  // ========================================

  resetToDefaults = () => {
    runInAction(() => {
      this.loads = [];
      this.selectedProjectId = null;
      this.selectedLoadId = null;
      this.filterByStatus = "all";
      this.searchTerm = "";
      this.error = null;
    });
    this.loadData();
  };
}

export default MMLoadStore;
